import React, { useRef, useEffect } from 'react';

const BRAND = '#6366f1';
const GOLD = '#FBBF24';
const GAIN = '#00FF94';
const LOSS = '#EF4444';
const CYAN = '#22d3ee';

const LAYER_COUNT = 7;
const NODES_PER_LAYER = [8, 12, 16, 20, 16, 12, 6];
const LABELS = ['Market\nData', 'Feature\nExtract', 'Pattern\nRecog', 'Deep\nAnalysis', 'Risk\nCalc', 'Signal\nGen', 'Trade\nAction'];
const INPUTS = ['RSI(14)', 'MACD', 'EMA 20', 'Vol Δ', 'OBV', 'ATR', 'BB Width', 'Momentum'];
const OUTPUTS = ['BUY', 'SELL', 'HOLD', 'Scale', 'Hedge', 'Exit'];
const OUT_COLORS = [GAIN, LOSS, GOLD, CYAN, BRAND, LOSS];
const DECISIONS = [
  { text: 'BUY AAPL', color: GAIN }, { text: 'SELL EUR/USD', color: LOSS },
  { text: 'HOLD BTC', color: GOLD }, { text: 'BUY XAU', color: GAIN },
  { text: 'SHORT SPX', color: LOSS }, { text: 'LONG SOL', color: GAIN },
  { text: 'EXIT ETH', color: LOSS }, { text: 'SCALE IN', color: CYAN },
  { text: 'HEDGE 40%', color: BRAND }, { text: 'REBALANCE', color: GOLD },
  { text: 'AI: 94.2%', color: CYAN }, { text: 'RISK: LOW', color: GAIN },
];

const NeuralHeroBg = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let W, H, animId, frame = 0;

    // Build network
    const layers = [];
    for (let l = 0; l < LAYER_COUNT; l++) {
      const nodes = [];
      for (let n = 0; n < NODES_PER_LAYER[l]; n++) {
        nodes.push({
          activation: Math.random(),
          pulsePhase: Math.random() * Math.PI * 2,
        });
      }
      layers.push({ nodes, label: LABELS[l] });
    }

    const layerSpacing = () => W / (LAYER_COUNT + 1);

    function getNodePos(li, ni) {
      const sp = layerSpacing();
      const x = sp * (li + 1);
      const count = layers[li].nodes.length;
      const totalH = H * 0.7;
      const startY = (H - totalH) / 2;
      const gap = totalH / (count + 1);
      return { x, y: startY + gap * (ni + 1) };
    }

    // Data streams
    const streams = [];
    function spawnStream() {
      const path = [];
      for (let l = 0; l < LAYER_COUNT; l++) path.push(Math.floor(Math.random() * layers[l].nodes.length));
      const colors = [BRAND, GOLD, GAIN, CYAN];
      streams.push({
        path, progress: 0,
        speed: 0.003 + Math.random() * 0.003,
        color: colors[Math.floor(Math.random() * colors.length)],
        thickness: 1 + Math.random() * 1.5,
      });
    }

    // Floating data chars
    const dataPoints = [];
    for (let i = 0; i < 50; i++) {
      dataPoints.push({
        x: Math.random(), y: Math.random(),
        vx: (Math.random() - 0.5) * 0.0003,
        vy: -0.0002 - Math.random() * 0.0003,
        char: ['0','1','$','%','+','-','.','/','=','<','>','∑','∆','λ','σ','μ','π','∞','≈','±'][Math.floor(Math.random() * 20)],
        opacity: 0.03 + Math.random() * 0.06,
        size: 8 + Math.random() * 10,
        rotation: Math.random() * 0.5 - 0.25,
      });
    }

    // Decision bubbles
    const bubbles = [];
    function spawnBubble() {
      const d = DECISIONS[Math.floor(Math.random() * DECISIONS.length)];
      bubbles.push({ ...d, x: W * 0.85 + Math.random() * W * 0.12, y: H * 0.15 + Math.random() * H * 0.7, life: 0, maxLife: 180 + Math.random() * 120, scale: 0 });
    }

    function drawHexGrid() {
      const size = 40;
      const h = size * Math.sqrt(3);
      ctx.strokeStyle = 'rgba(99, 102, 241, 0.015)';
      ctx.lineWidth = 0.5;
      for (let row = -1; row * h * 0.75 < H + size; row++) {
        for (let col = -1; col * size * 1.5 < W + size; col++) {
          const cx = col * size * 1.5;
          const cy = row * h + (col % 2 ? h * 0.5 : 0);
          ctx.beginPath();
          for (let i = 0; i < 6; i++) {
            const angle = Math.PI / 3 * i - Math.PI / 6;
            const px = cx + size * 0.4 * Math.cos(angle);
            const py = cy + size * 0.4 * Math.sin(angle);
            if (i === 0) ctx.moveTo(px, py); else ctx.lineTo(px, py);
          }
          ctx.closePath();
          ctx.stroke();
        }
      }
    }

    function resize() {
      const dpr = window.devicePixelRatio || 1;
      const parent = canvas.parentElement;
      if (!parent) return;
      W = parent.clientWidth;
      H = parent.clientHeight;
      canvas.width = W * dpr;
      canvas.height = H * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    }

    function draw() {
      ctx.clearRect(0, 0, W, H);
      frame++;

      if (frame % 12 === 0) spawnStream();
      if (frame % 200 === 0) spawnBubble();
      if (frame === 1) { spawnBubble(); spawnBubble(); }

      drawHexGrid();

      // Floating chars
      ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      for (const dp of dataPoints) {
        dp.x += dp.vx; dp.y += dp.vy;
        if (dp.y < -0.05) { dp.y = 1.05; dp.x = Math.random(); }
        if (dp.x < -0.05) dp.x = 1.05;
        if (dp.x > 1.05) dp.x = -0.05;
        ctx.save();
        ctx.translate(dp.x * W, dp.y * H);
        ctx.rotate(dp.rotation);
        ctx.font = `${dp.size}px monospace`;
        ctx.fillStyle = `rgba(99, 102, 241, ${dp.opacity})`;
        ctx.fillText(dp.char, 0, 0);
        ctx.restore();
      }

      // Connections
      for (let l = 0; l < LAYER_COUNT - 1; l++) {
        const fromNodes = layers[l].nodes;
        const toNodes = layers[l + 1].nodes;
        for (let fi = 0; fi < fromNodes.length; fi++) {
          for (let c = 0; c < 3; c++) {
            const ti = (fi * 3 + c) % toNodes.length;
            const from = getNodePos(l, fi);
            const to = getNodePos(l + 1, ti);
            const act = (fromNodes[fi].activation + toNodes[ti].activation) / 2;
            ctx.strokeStyle = `rgba(99, 102, 241, ${0.01 + act * 0.03})`;
            ctx.lineWidth = 0.3 + act * 0.3;
            ctx.beginPath();
            ctx.moveTo(from.x, from.y);
            const mx = (from.x + to.x) / 2;
            const my = (from.y + to.y) / 2 + Math.sin(fi + l) * 15;
            ctx.quadraticCurveTo(mx, my, to.x, to.y);
            ctx.stroke();
          }
        }
      }

      // Data streams
      for (let s = streams.length - 1; s >= 0; s--) {
        const stream = streams[s];
        stream.progress += stream.speed;
        if (stream.progress > LAYER_COUNT - 1) { streams.splice(s, 1); continue; }
        const cl = Math.floor(stream.progress);
        const t = stream.progress - cl;
        const nl = Math.min(cl + 1, LAYER_COUNT - 1);
        const from = getNodePos(cl, stream.path[cl]);
        const to = getNodePos(nl, stream.path[nl]);
        const mx = (from.x + to.x) / 2;
        const my = (from.y + to.y) / 2;
        const x = (1 - t) * (1 - t) * from.x + 2 * (1 - t) * t * mx + t * t * to.x;
        const y = (1 - t) * (1 - t) * from.y + 2 * (1 - t) * t * my + t * t * to.y;

        const g = ctx.createRadialGradient(x, y, 0, x, y, 15);
        g.addColorStop(0, stream.color + '55');
        g.addColorStop(0.5, stream.color + '15');
        g.addColorStop(1, 'transparent');
        ctx.fillStyle = g;
        ctx.beginPath(); ctx.arc(x, y, 15, 0, Math.PI * 2); ctx.fill();

        ctx.fillStyle = stream.color;
        ctx.beginPath(); ctx.arc(x, y, stream.thickness, 0, Math.PI * 2); ctx.fill();
        ctx.fillStyle = '#fff';
        ctx.beginPath(); ctx.arc(x, y, stream.thickness * 0.4, 0, Math.PI * 2); ctx.fill();

        // Trail
        for (let tr = 1; tr <= 6; tr++) {
          const tt = stream.progress - tr * 0.02;
          if (tt < 0) break;
          const tl = Math.floor(tt);
          const tf = tt - tl;
          const tnl = Math.min(tl + 1, LAYER_COUNT - 1);
          if (tl >= LAYER_COUNT) break;
          const f2 = getNodePos(tl, stream.path[tl]);
          const t2 = getNodePos(tnl, stream.path[tnl]);
          const mx2 = (f2.x + t2.x) / 2, my2 = (f2.y + t2.y) / 2;
          const tx = (1 - tf) * (1 - tf) * f2.x + 2 * (1 - tf) * tf * mx2 + tf * tf * t2.x;
          const ty = (1 - tf) * (1 - tf) * f2.y + 2 * (1 - tf) * tf * my2 + tf * tf * t2.y;
          ctx.fillStyle = stream.color;
          ctx.globalAlpha = 0.3 - tr * 0.045;
          ctx.beginPath(); ctx.arc(tx, ty, stream.thickness * (1 - tr * 0.12), 0, Math.PI * 2); ctx.fill();
          ctx.globalAlpha = 1;
        }

        if (cl < LAYER_COUNT) {
          layers[cl].nodes[stream.path[cl]].activation =
            Math.min(1, layers[cl].nodes[stream.path[cl]].activation + 0.15);
        }
      }

      // Nodes
      for (let l = 0; l < LAYER_COUNT; l++) {
        const layer = layers[l];
        for (let n = 0; n < layer.nodes.length; n++) {
          const node = layer.nodes[n];
          const { x, y } = getNodePos(l, n);
          node.activation *= 0.98;
          const pulse = Math.sin(frame * 0.04 + node.pulsePhase) * 0.3 + 0.7;
          const act = node.activation;
          const radius = 3 + act * 4;

          if (act > 0.2) {
            const glow = ctx.createRadialGradient(x, y, 0, x, y, radius * 4);
            const col = l === LAYER_COUNT - 1 ? GAIN : BRAND;
            glow.addColorStop(0, col + Math.round(act * 30).toString(16).padStart(2, '0'));
            glow.addColorStop(1, 'transparent');
            ctx.fillStyle = glow;
            ctx.beginPath(); ctx.arc(x, y, radius * 4, 0, Math.PI * 2); ctx.fill();
          }

          ctx.strokeStyle = `rgba(99, 102, 241, ${0.08 + act * 0.25})`;
          ctx.lineWidth = 0.6;
          ctx.beginPath(); ctx.arc(x, y, radius + 2, 0, Math.PI * 2); ctx.stroke();

          const coreAlpha = 0.15 + act * 0.6;
          const coreColor = l === 0 ? CYAN : l === LAYER_COUNT - 1 ? GOLD : BRAND;
          ctx.fillStyle = coreColor;
          ctx.globalAlpha = coreAlpha * pulse;
          ctx.beginPath(); ctx.arc(x, y, radius, 0, Math.PI * 2); ctx.fill();
          ctx.globalAlpha = 1;

          if (act > 0.3) {
            ctx.fillStyle = '#fff';
            ctx.globalAlpha = act * 0.6;
            ctx.beginPath(); ctx.arc(x, y, radius * 0.35, 0, Math.PI * 2); ctx.fill();
            ctx.globalAlpha = 1;
          }
        }

        // Layer label
        const lx = layerSpacing() * (l + 1);
        ctx.font = '9px monospace';
        ctx.fillStyle = 'rgba(255,255,255,0.12)';
        ctx.textAlign = 'center';
        layer.label.split('\n').forEach((line, i) => {
          ctx.fillText(line, lx, H * 0.92 + i * 12);
        });
      }

      // Decision bubbles
      for (let b = bubbles.length - 1; b >= 0; b--) {
        const bub = bubbles[b];
        bub.life++;
        if (bub.life > bub.maxLife) { bubbles.splice(b, 1); continue; }
        const fadeIn = Math.min(1, bub.life / 30);
        const fadeOut = Math.min(1, (bub.maxLife - bub.life) / 30);
        const alpha = fadeIn * fadeOut * 0.6;
        bub.scale = fadeIn;
        bub.y -= 0.15;

        ctx.save();
        ctx.translate(bub.x, bub.y);
        ctx.scale(bub.scale, bub.scale);
        ctx.globalAlpha = alpha;

        const tw = ctx.measureText(bub.text).width;
        const pw = tw + 20;
        const ph = 24;
        ctx.fillStyle = 'rgba(10, 14, 26, 0.6)';
        ctx.beginPath(); ctx.roundRect(-pw / 2, -ph / 2, pw, ph, ph / 2); ctx.fill();
        ctx.strokeStyle = bub.color + '44';
        ctx.lineWidth = 1;
        ctx.beginPath(); ctx.roundRect(-pw / 2, -ph / 2, pw, ph, ph / 2); ctx.stroke();
        ctx.fillStyle = bub.color;
        ctx.beginPath(); ctx.arc(-pw / 2 + 10, 0, 3, 0, Math.PI * 2); ctx.fill();
        ctx.font = 'bold 10px monospace';
        ctx.fillStyle = bub.color;
        ctx.textAlign = 'center';
        ctx.fillText(bub.text, 3, 4);

        ctx.restore();
        ctx.globalAlpha = 1;
      }

      // Input labels
      ctx.font = '8px monospace';
      ctx.textAlign = 'right';
      for (let i = 0; i < Math.min(INPUTS.length, layers[0].nodes.length); i++) {
        const { x, y } = getNodePos(0, i);
        const act = layers[0].nodes[i].activation;
        ctx.fillStyle = `rgba(34, 211, 238, ${0.15 + act * 0.3})`;
        ctx.fillText(INPUTS[i], x - 16, y + 3);
        const val = (Math.sin(frame * 0.02 + i * 1.3) * 50 + 50).toFixed(1);
        ctx.fillStyle = `rgba(255,255,255, ${0.08 + act * 0.15})`;
        ctx.textAlign = 'left';
        ctx.fillText(val, x - 60, y + 3);
        ctx.textAlign = 'right';
      }

      // Output labels
      ctx.textAlign = 'left';
      for (let i = 0; i < Math.min(OUTPUTS.length, layers[LAYER_COUNT - 1].nodes.length); i++) {
        const { x, y } = getNodePos(LAYER_COUNT - 1, i);
        const act = layers[LAYER_COUNT - 1].nodes[i].activation;
        ctx.fillStyle = OUT_COLORS[i];
        ctx.globalAlpha = 0.15 + act * 0.5;
        ctx.font = 'bold 9px monospace';
        ctx.fillText(OUTPUTS[i], x + 16, y + 3);
        const barW = 30, barH = 4, bx = x + 55, by = y - 2;
        ctx.fillStyle = 'rgba(255,255,255,0.04)';
        ctx.fillRect(bx, by, barW, barH);
        ctx.fillStyle = OUT_COLORS[i];
        ctx.fillRect(bx, by, barW * act, barH);
        ctx.globalAlpha = 1;
      }

      // Scan line
      const scanX = (frame * 0.8) % W;
      const scanGrad = ctx.createLinearGradient(scanX - 3, 0, scanX + 3, 0);
      scanGrad.addColorStop(0, 'transparent');
      scanGrad.addColorStop(0.5, 'rgba(99, 102, 241, 0.025)');
      scanGrad.addColorStop(1, 'transparent');
      ctx.fillStyle = scanGrad;
      ctx.fillRect(scanX - 3, 0, 6, H);

      animId = requestAnimationFrame(draw);
    }

    resize();
    draw();
    window.addEventListener('resize', resize);
    return () => {
      window.removeEventListener('resize', resize);
      cancelAnimationFrame(animId);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
      }}
    />
  );
};

export default NeuralHeroBg;
